/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import SkeletonLoaderView from '@airasia-phoenix/react-native/components/SkeletonLoaderView';
import { useFlagEnabled } from '@airasia-rn/account';
import { FEATURE_FLAG_HOME_ENABLE_ADS_RENDER } from '../../constants';
import { styles } from './styles';

const WEB_AD_SIZES = {
  desktopLeaderboard: [970, 90] as googletag.SingleSizeArray,
  tabletLeaderboard: [728, 90] as googletag.SingleSizeArray,
  rectangleBanner: [570, 180] as googletag.SingleSizeArray,
  mediumRectangle: [300, 250] as googletag.SingleSizeArray,
  mobileFooter: [360, 180] as googletag.SingleSizeArray,
};

/**
 * Resolves once the browser is idle, or after a bounded wait.
 *
 * gpt.js is ~206kB (about 123kB of it unused) and Lighthouse attributed roughly
 * 110ms of blocking time to it on the home page. No ad needs to be set up while
 * React is still hydrating, so slot setup waits for the main thread to go quiet
 * even for a slot that is already on screen.
 */
let idlePromise: Promise<void> | null = null;

const whenIdle = (): Promise<void> => {
  if (idlePromise) return idlePromise;
  idlePromise = new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve();
    const ric = (
      window as unknown as {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number;
      }
    ).requestIdleCallback;
    if (typeof ric === 'function') ric(() => resolve(), { timeout: 3000 });
    else setTimeout(resolve, 2000);
  });
  return idlePromise;
};

let gptLoadPromise: Promise<typeof googletag> | null = null;

const loadGPT = () => {
  if (gptLoadPromise) return gptLoadPromise;

  gptLoadPromise = new Promise<typeof googletag>((resolve, reject) => {
    if (window.googletag?.apiReady) {
      resolve(window.googletag);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://www.googletagservices.com/tag/js/gpt.js"]',
    );

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://www.googletagservices.com/tag/js/gpt.js';
      script.async = true;
      script.onload = () => {
        if (!window.googletag || !window.googletag.cmd) {
          reject(new Error('GPT loaded but googletag is missing'));
          return;
        }

        window.googletag.cmd.push(() => {
          resolve(window.googletag);
        });
      };

      script.onerror = reject;
      document.head.appendChild(script);
    } else {
      // Script exists - check if already loaded
      if (window.googletag) {
        resolve(window.googletag);
      } else {
        // Wait for load event
        existingScript.addEventListener('load', () => {
          window.googletag =
            window.googletag ||
            ({ cmd: [], apiReady: true } as unknown as typeof googletag);
          resolve(window.googletag);
        });
        existingScript.addEventListener('error', reject);
      }
    }
  });

  // Reset the cache if the load fails, so we can retry later
  gptLoadPromise.catch(() => {
    gptLoadPromise = null;
  });

  return gptLoadPromise;
};

let servicesEnabled = false;

const ensureGPTServices = (googletagInstance: typeof googletag) => {
  if (servicesEnabled) return;

  googletagInstance.pubads().collapseEmptyDivs();
  googletagInstance.enableServices();
  servicesEnabled = true;
};

export type HomeWebAdProps = {
  adUnit: string;
  lobItemId?: string;
  slotId?: string;
  isFooterAds?: boolean;
  /**
   * If true, keeps the slot collapsed (no visible gap) until GPT confirms a
   * non-empty render for this slot.
   */
  collapseUntilRendered?: boolean;
  bottomSpacing?: number;
  onAdFailed?: (lobItemId: string) => void;
};

const HomeWebAd = ({
  adUnit,
  lobItemId = '',
  slotId = 'home-ad',
  isFooterAds = false,
  collapseUntilRendered = false,
  bottomSpacing = 0,
  onAdFailed,
}: HomeWebAdProps) => {
  const { enabled: isAdsRenderEnabled } = useFlagEnabled(
    FEATURE_FLAG_HOME_ENABLE_ADS_RENDER,
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [loading, setLoading] = useState(true);
  const [adFailed, setAdFailed] = useState(false);
  const [rendered, setRendered] = useState(false);
  // Set once the slot is close enough to the viewport to be worth loading.
  const [inRange, setInRange] = useState(false);

  const notifyAdFailed = useCallback(
    (id: string) => {
      setAdFailed(true);
      onAdFailed?.(id);
    },
    [onAdFailed],
  );

  useEffect(() => {
    if (typeof document === 'undefined' || !isAdsRenderEnabled) return;

    const styleId = `style-${slotId}`;
    const styleTag = document.createElement('style');
    styleTag.id = styleId;

    styleTag.innerHTML = `
      #${slotId} {
        border-radius: 12px !important;
        overflow: hidden !important;
      }
      #${slotId} iframe {
        border-radius: 12px !important;
      }
    `;

    document.head.appendChild(styleTag);
    return () => {
      if (typeof document !== 'undefined') {
        const el = document.getElementById(styleId);
        if (el) el.remove();
      }
    };
  }, [slotId, isAdsRenderEnabled]);

  useEffect(() => {
    setLoading(true);
    setAdFailed(false);
    setRendered(false);
  }, [adUnit, slotId, isFooterAds, isAdsRenderEnabled]);

  // ResizeObserver
  useEffect(() => {
    if (!adUnit || !isAdsRenderEnabled) return;

    if (typeof ResizeObserver === 'undefined') {
      notifyAdFailed(lobItemId);
      return;
    }
    const element = containerRef.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const width = Math.round(entries[0].contentRect.width);
      if (width === 0) {
        return;
      }
      setContainerWidth(width);
    });

    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, [
    adUnit,
    slotId,
    lobItemId,
    notifyAdFailed,
    isFooterAds,
    isAdsRenderEnabled,
  ]);

  // Viewport gate: only slots near the viewport are worth fetching. The
  // container is full width with a 1px minimum even while collapsed, so it has
  // a real box for the observer to intersect.
  useEffect(() => {
    if (!adUnit || !isAdsRenderEnabled) return;

    if (typeof IntersectionObserver === 'undefined') {
      setInRange(true);
      return;
    }

    const element = containerRef.current;
    if (!element) return;

    // A generous margin so the creative is ready just before it scrolls into
    // view; slots below the fold cost nothing during page load.
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInRange(true);
          intersectionObserver.disconnect();
        }
      },
      { rootMargin: '600px' },
    );

    intersectionObserver.observe(element);
    return () => intersectionObserver.disconnect();
  }, [adUnit, slotId, isAdsRenderEnabled]);

  // Load GPT & define slot whenever width changes
  useEffect(() => {
    if (!adUnit || containerWidth === 0 || !inRange || !isAdsRenderEnabled)
      return;

    let cancelled = false;
    let slot: googletag.Slot | null = null;
    let googletagRef: typeof googletag | null = null;

    const handleSlotRenderEnded = (
      event: googletag.events.SlotRenderEndedEvent,
    ) => {
      if (event.slot.getSlotElementId() !== slotId) return;
      if (cancelled) return;
      setLoading(false);
      if (event.isEmpty) {
        notifyAdFailed(lobItemId);
        return;
      }
      setRendered(true);
    };

    whenIdle()
      .then(() => loadGPT())
      .then((googletagInstance) => {
        if (cancelled) return;
        googletagRef = googletagInstance;

        googletagInstance.cmd.push(() => {
          ensureGPTServices(googletagInstance);

          googletagInstance
            .pubads()
            .addEventListener('slotRenderEnded', handleSlotRenderEnded);

          const sizeMappingBuilder = googletagInstance.sizeMapping();

          if (isFooterAds) {
            sizeMappingBuilder
              .addSize([570, 0], [WEB_AD_SIZES.rectangleBanner])
              .addSize([0, 0], [WEB_AD_SIZES.mobileFooter]);
          } else {
            sizeMappingBuilder
              .addSize([990, 0], [WEB_AD_SIZES.desktopLeaderboard])
              .addSize([748, 0], [WEB_AD_SIZES.tabletLeaderboard])
              .addSize([570, 0], [WEB_AD_SIZES.rectangleBanner])
              .addSize([0, 0], [WEB_AD_SIZES.mediumRectangle]);
          }

          const sizeMapping = sizeMappingBuilder.build();
          if (!sizeMapping) {
            setLoading(false);
            notifyAdFailed(lobItemId);
            return;
          }

          const allSizes: googletag.MultiSize = isFooterAds
            ? [WEB_AD_SIZES.rectangleBanner, WEB_AD_SIZES.mobileFooter]
            : [
                WEB_AD_SIZES.desktopLeaderboard,
                WEB_AD_SIZES.tabletLeaderboard,
                WEB_AD_SIZES.rectangleBanner,
                WEB_AD_SIZES.mediumRectangle,
              ];

          slot = googletagInstance.defineSlot(adUnit, allSizes, slotId);

          if (!slot) {
            setLoading(false);
            notifyAdFailed(lobItemId);
            return;
          }

          slot.defineSizeMapping(sizeMapping);
          slot.addService(googletagInstance.pubads());
          if (!collapseUntilRendered) {
            setLoading(false);
          }

          if (isFooterAds) {
            setTimeout(() => {
              if (cancelled) return;
              googletagInstance.cmd.push(() => {
                googletagInstance.pubads().updateCorrelator();
                googletagInstance.display(slotId);
              });
            }, 0);
          } else {
            googletagInstance.display(slotId);
          }
        });
      })
      .catch((error) => {
        console.error('Failed to load GPT:', error);
        setLoading(false);
        notifyAdFailed(lobItemId);
      });

    return () => {
      cancelled = true;
      if (slot) {
        window.googletag?.cmd.push(() => {
          try {
            window.googletag.destroySlots([slot!]);
          } catch (e) {
            console.warn('Failed to destroy GPT slot on unmount:', e);
          }
        });
      }
      try {
        (googletagRef ?? window.googletag)
          ?.pubads?.()
          ?.removeEventListener?.('slotRenderEnded', handleSlotRenderEnded);
      } catch {
        // noop
      }
    };
  }, [
    adUnit,
    containerWidth,
    inRange,
    slotId,
    lobItemId,
    notifyAdFailed,
    isFooterAds,
    collapseUntilRendered,
    isAdsRenderEnabled,
  ]);

  if (!adUnit || adFailed || !isAdsRenderEnabled) return null;

  return (
    <View
      ref={(node) => {
        containerRef.current = node as unknown as HTMLDivElement | null;
      }}
      style={[
        styles.container,
        rendered && bottomSpacing > 0 ? { marginBottom: bottomSpacing } : null,
        collapseUntilRendered && !rendered
          ? { minHeight: 1, height: 1, opacity: 0, overflow: 'hidden' }
          : null,
      ]}
    >
      <View nativeID={slotId} style={styles.container} />
      {loading && (
        <View style={styles.loading}>
          <SkeletonLoaderView style={styles.skeletonLoader} />
        </View>
      )}
    </View>
  );
};

export default HomeWebAd;
